import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import NavigationSidebar from "../ui/NavigationSidebar";
import MatchBotLoader from "../ui/MatchBotLoader";
import BillingNotice from "../BillingNotice";
import { useMinimumLoading } from "../../lib/useMinimumLoading";

const TenantLayout = () => {
  const location = useLocation();
  const isRouteSettling = useMinimumLoading(false, {
    minimumMs: 900,
    resetKey: location.pathname,
  });
  
  // Estado persistente del Sidebar (Desktop)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });

  // Estado del menú móvil (Independiente)
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Guardar preferencia de colapso
  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  // Cerrar menú móvil al cambiar de ruta automáticamente
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // El panel bloquea el scroll del documento (cada página scrollea por dentro). Sólo mientras está
  // montado: el sitio público tiene que scrollear normal.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("app-shell");
    return () => root.classList.remove("app-shell");
  }, []);

  // Safari desplaza el viewport por su cuenta al enfocar. Sólo detectamos el teclado
  // para quitar el safe-area inferior; redimensionar además el layout provoca un doble salto.
  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const syncKeyboardState = () => {
      const visibleHeight = viewport?.height || window.innerHeight;
      const keyboardHeight = window.innerHeight - visibleHeight - (viewport?.offsetTop || 0);
      root.dataset.keyboardOpen = keyboardHeight > 150 ? "true" : "false";
    };
    syncKeyboardState();
    viewport?.addEventListener("resize", syncKeyboardState);
    window.addEventListener("resize", syncKeyboardState);
    return () => {
      viewport?.removeEventListener("resize", syncKeyboardState);
      window.removeEventListener("resize", syncKeyboardState);
      delete root.dataset.keyboardOpen;
    };
  }, []);

  return (
    // Alto real del viewport en móvil: 100dvh por estilo inline (gana sobre las clases,
    // que no se aplicaban por orden de CSS y dejaban el composer detrás de la barra de Safari).
    // h-screen queda como fallback para navegadores sin soporte de dvh.
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ height: "100dvh" }}>
      {isRouteSettling && <MatchBotLoader />}
      
      {/* 1. SIDEBAR */}
      <NavigationSidebar 
        isCollapsed={isCollapsed} 
        onToggleCollapse={toggleCollapse}
        isMobileOpen={isMobileOpen}
        onMobileClose={() => setIsMobileOpen(false)}
      />

      {/* 2. AREA DE CONTENIDO */}
      {/* 👇 CAMBIO CLAVE 2: Agregamos 'relative' y mantenemos 'overflow-hidden' aquí */}
      {/* Esto crea el marco fijo. La página hija (Outlet) será la que tenga el scroll (overflow-y-auto) */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out relative overflow-hidden ${isCollapsed ? "md:ml-[72px]" : "md:ml-[272px]"}`}>

        {/* Aviso de cuenta caída (trial vencido, pausada, mora): va ARRIBA del Outlet y fuera de su
            scroll, para que no dependa de que la página de turno lo muestre ni se pierda scrolleando.
            Se dibuja solo cuando corresponde; en el caso normal no ocupa nada. */}
        <BillingNotice />

        {/* Las páginas se renderizan aquí y ellas controlan su propio scroll interno */}
        <Outlet context={{ 
            isSidebarCollapsed: isCollapsed, 
            toggleMobileMenu: () => setIsMobileOpen(!isMobileOpen) 
        }} />
        
      </div>

      {/* Overlay Móvil */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 md:hidden animate-in fade-in"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </div>
  );
};

export default TenantLayout;
